import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  CrossSectionIcon,
  Flex,
  SelectionToolIcon,
  Slider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
  SwapVerticalIcon,
  Tooltip,
} from '@frontend/legacy-components';

import ViewerButton from '../components/ViewerButton';
import ViewerButtonTooltip from '../components/ViewerButtonTooltip';
import ViewerToolPanel from '../components/ViewerToolPanel';
import { crossSection } from '../data/actions';
import {
  getCrossSectionEnabled,
  getCrossSectionPickMode,
  getCrossSectionPosition,
  getIsWireFrameToolActive,
} from '../data/reducer';

export type CrossSectionToolProps = {
  isEnabled: boolean;
  /** `toolbar` — icon in the main tool strip; `floating` — depth slider + actions below the strip */
  placement: 'toolbar' | 'floating';
};

/**
 * Cross-section: toggle in the toolbar and, when on, the floating depth / face controls.
 * Two JSX nodes via `placement` because the tool strip and floating panel mount in different places.
 */
const CrossSectionTool = ({ isEnabled, placement }: CrossSectionToolProps) => {
  const enabled = useSelector(getCrossSectionEnabled);
  const pickMode = useSelector(getCrossSectionPickMode);
  const position = useSelector(getCrossSectionPosition);
  const isWireFrameActive = useSelector(getIsWireFrameToolActive);
  const dispatch = useDispatch();

  const facePickBlockedByWireframe = isWireFrameActive;

  const toggleEnabled = useCallback(() => {
    dispatch(crossSection.setEnabled(!enabled));
  }, [dispatch, enabled]);

  if (!isEnabled) {
    return null;
  }

  if (placement === 'toolbar') {
    return (
      <Tooltip
        placement="bottom"
        label={
          <ViewerButtonTooltip
            firstLine="Cross-section tool"
            secondLine="Slice through the part"
          />
        }
      >
        <ViewerButton
          isActive={enabled}
          aria-label="Cross-section tool"
          onClick={toggleEnabled}
        >
          <CrossSectionIcon boxSize={3} />
        </ViewerButton>
      </Tooltip>
    );
  }

  if (!enabled) {
    return null;
  }

  return (
    <ViewerToolPanel
      data-testid="cross-section-toolbar"
      direction="column"
      gap={1}
    >
      <Flex align="center" gap={2}>
        <Tooltip
          placement="top"
          label={
            pickMode
              ? 'Depth is fixed while picking a face'
              : 'Move the cutting plane through the part'
          }
        >
          <Flex align="center" p={0.5}>
            <Slider
              aria-label="Cross-section depth"
              focusThumbOnChange={false}
              isDisabled={pickMode}
              min={0}
              max={1}
              step={0.01}
              value={position}
              onChange={(n) => dispatch(crossSection.setPosition(n))}
              w="200px"
            >
              <SliderTrack>
                <SliderFilledTrack />
              </SliderTrack>
              <SliderThumb />
            </Slider>
          </Flex>
        </Tooltip>
        <Flex align="center" gap={0.5} flexShrink={0}>
          <Tooltip
            placement="top"
            label={
              facePickBlockedByWireframe
                ? 'Turn off wireframe to pick a face for section direction'
                : pickMode
                ? 'Click a face to set section direction (Esc to cancel)'
                : 'Pick face normal as section direction'
            }
          >
            <ViewerButton
              aria-label={
                facePickBlockedByWireframe
                  ? 'Pick face for section direction (unavailable in wireframe view)'
                  : pickMode
                  ? 'Picking face for section direction; press Esc to cancel'
                  : 'Pick face for section direction'
              }
              disabled={facePickBlockedByWireframe}
              isActive={pickMode}
              onClick={() => dispatch(crossSection.setPickMode(!pickMode))}
            >
              <SelectionToolIcon boxSize={3} />
            </ViewerButton>
          </Tooltip>
          <Tooltip
            placement="top"
            label={
              pickMode
                ? 'Swap side is disabled while picking a face'
                : 'Swap which side of the plane is visible'
            }
          >
            <ViewerButton
              aria-label="Swap which side of the plane is visible"
              disabled={pickMode}
              onClick={() => dispatch(crossSection.toggleInverted())}
            >
              <SwapVerticalIcon boxSize={3} />
            </ViewerButton>
          </Tooltip>
        </Flex>
      </Flex>
    </ViewerToolPanel>
  );
};

export default CrossSectionTool;

/** Standalone enable control (e.g. hosts without Redux toolbar); not used by the main CAD viewer. */
export const CrossSectionEnableButton = ({
  enabled,
  onEnabledChange,
}: {
  enabled: boolean;
  onEnabledChange: (enabled: boolean) => void;
}) => {
  const toggle = useCallback(() => {
    onEnabledChange(!enabled);
  }, [enabled, onEnabledChange]);

  return (
    <Tooltip
      placement="bottom"
      label={
        enabled
          ? 'Disable cross-section view'
          : 'Enable cross-section view (slice through the part)'
      }
    >
      <Flex
        as="button"
        type="button"
        aria-pressed={enabled}
        aria-label="Cross-section tool"
        onClick={toggle}
        align="center"
        justify="center"
        w="32px"
        h="32px"
        borderRadius="4px"
        cursor="pointer"
        bg={enabled ? 'rgba(38, 138, 245, 0.15)' : '#fff'}
        border={enabled ? '2px solid #268af5' : '1px solid #ccc'}
        color={enabled ? '#268af5' : '#444'}
      >
        <CrossSectionIcon boxSize="18px" />
      </Flex>
    </Tooltip>
  );
};
